import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type ViewMode = 'grid' | 'list';

@Injectable({
    providedIn: 'root'
})
export class ViewModeService {
    private readonly STORAGE_KEY = 'grid_view_mode';
    private viewModeSubject = new BehaviorSubject<ViewMode>('grid');
    
    public viewMode$: Observable<ViewMode> = this.viewModeSubject.asObservable();
    
    constructor() {
        this.loadViewModeFromStorage();
    }
    
    /**
     * Get the current view mode
     */
    public get viewMode(): ViewMode {
        return this.viewModeSubject.value;
    }
    
    /**
     * Set the view mode and persist to storage
     */
    public setViewMode(mode: ViewMode): void {
        if (mode !== this.viewModeSubject.value) {
            this.viewModeSubject.next(mode);
            this.saveViewModeToStorage(mode);
        }
    }
    
    /**
     * Get the current view mode preference from storage
     */
    public getViewModePreference(): ViewMode {
        try {
            const savedViewMode = localStorage.getItem(this.STORAGE_KEY);
            return (savedViewMode === 'grid' || savedViewMode === 'list') ? savedViewMode : 'grid';
        } catch (error) {
            console.warn('Could not get view mode from storage:', error);
            return 'grid';
        }
    }
    
    /**
     * Clear the view mode preference from storage
     */
    public clearViewModePreference(): void {
        try {
            localStorage.removeItem(this.STORAGE_KEY);
            this.viewModeSubject.next('grid');
        } catch (error) {
            console.warn('Could not clear view mode from storage:', error);
        }
    }
    
    /**
     * Check if the current view mode matches the given mode
     */
    public isViewMode(mode: ViewMode): boolean {
        return this.viewModeSubject.value === mode;
    }
    
    /**
     * Toggle between grid and list view modes
     */
    public toggleViewMode(): ViewMode {
        const newMode: ViewMode = this.viewModeSubject.value === 'grid' ? 'list' : 'grid';
        this.setViewMode(newMode);
        return newMode;
    }
    
    /**
     * Load view mode from localStorage
     */
    private loadViewModeFromStorage(): void {
        try {
            const savedViewMode = this.getViewModePreference();
            this.viewModeSubject.next(savedViewMode);
        } catch (error) {
            console.warn('Could not load view mode from storage:', error);
        }
    }
    
    /**
     * Save view mode to localStorage
     */
    private saveViewModeToStorage(mode: ViewMode): void {
        try {
            localStorage.setItem(this.STORAGE_KEY, mode);
        } catch (error) {
            console.warn('Could not save view mode to storage:', error);
        }
    }
}
